<script setup lang="ts">
import PortfolioBodyElementBase from './PortfolioBodyElementBase.vue';
import BaseAmountFromAsset from '@/components/BaseAmountFromAsset.vue';
import BaseAssetFromAsset from '@/components/BaseAssetFromAsset.vue';
import BasePrice from '@/components/BasePrice.vue';
import BaseValue from '@/components/BaseValue.vue';
import BaseYields from '@/components/BaseYields.vue';
import BaseHealth from '@/components/BaseHealth.vue';
import { PortfolioElementBorrowLend } from '@sonarwatch/portfolio-core';
import { getExpireOnTagDetail } from '@/helpers/getExpireOnTagDetail';
import { computed } from 'vue';
import { TagDetails } from '@/types';
const props = defineProps<{
  element: PortfolioElementBorrowLend;
  owner?: string;
}>();

const tags = computed(() => {
  const tags: TagDetails[] = [];
  if (props.element.data.expireOn) {
    tags.push(getExpireOnTagDetail(props.element.data.expireOn));
  }
  return tags;
});
</script>

<template>
  <PortfolioBodyElementBase
    :label="element.label"
    :network-id="element.networkId"
    :owner="owner"
    :tags="tags"
  >
    <template #header>
      <span>
        {{ element.name }}
      </span>
    </template>
    <template #default>
      <BaseHealth
        class="text-caption text-medium-emphasis d-flex align-baseline justify-end mx-4"
        :health-ratio="element.data.healthRatio"
      ></BaseHealth>
      <div>
        <v-table hover>
          <template v-if="element.data.suppliedAssets.length > 0">
            <thead>
              <tr>
                <th class="text-left text-no-wrap">
                  Supplied
                  <BaseValue
                    class="text-caption text-medium-emphasis ml-2"
                    :value="element.data.suppliedValue"
                  />
                </th>
                <th class="text-left">Balance</th>
                <th class="text-right">Price</th>
                <th class="text-right">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(asset, i) in element.data.suppliedAssets" :key="i">
                <td class="d-flex align-center">
                  <BaseAssetFromAsset style="min-width: 72px" :asset="asset" />
                  <BaseYields
                    class="ml-2"
                    :yields="element.data.suppliedYields[i]"
                    size="x-small"
                  />
                </td>
                <td class="text-left">
                  <BaseAmountFromAsset :asset="asset" />
                </td>
                <td class="text-right">
                  <BasePrice :asset="asset" />
                </td>
                <td class="text-right">
                  <BaseValue :value="asset.value" />
                </td>
              </tr>
            </tbody>
          </template>
          <template v-if="element.data.borrowedAssets.length > 0">
            <thead>
              <tr>
                <th class="text-left text-no-wrap">
                  Borrowed
                  <BaseValue
                    class="text-caption text-medium-emphasis ml-2"
                    :value="element.data.borrowedValue"
                  />
                </th>
                <th class="text-left">Balance</th>
                <th class="text-right">Price</th>
                <th class="text-right">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(asset, i) in element.data.borrowedAssets" :key="i">
                <td class="d-flex align-center">
                  <BaseAssetFromAsset style="min-width: 72px" :asset="asset" />
                  <BaseYields
                    class="ml-2"
                    :yields="element.data.borrowedYields[i]"
                    size="x-small"
                  />
                </td>
                <td class="text-left">
                  <BaseAmountFromAsset :asset="asset" />
                </td>
                <td class="text-right">
                  <BasePrice :asset="asset" />
                </td>
                <td class="text-right">
                  <BaseValue :value="asset.value" />
                </td>
              </tr>
            </tbody>
          </template>
          <template
            v-if="
              element.data.unsettled && element.data.unsettled.assets.length > 0
            "
          >
            <thead>
              <tr>
                <th class="text-left text-no-wrap">
                  Unsettled
                  <BaseValue
                    class="text-caption text-medium-emphasis ml-2"
                    :value="element.data.unsettled.value"
                  />
                </th>
                <th></th>
                <th></th>
                <th class="text-right">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(asset, i) in element.data.unsettled.assets" :key="i">
                <td>
                  <BaseAssetFromAsset style="min-width: 72px" :asset="asset" />
                </td>
                <td></td>
                <td></td>
                <td class="text-right">
                  <BaseValue :value="asset.value" />
                </td>
              </tr>
            </tbody>
          </template>
          <template v-if="element.data.rewardAssets.length > 0">
            <thead>
              <tr>
                <th class="text-left text-no-wrap">
                  Rewards
                  <BaseValue
                    class="text-caption text-medium-emphasis ml-2"
                    :value="element.data.rewardValue"
                  />
                </th>
                <th class="text-left">Balance</th>
                <th class="text-right">Price</th>
                <th class="text-right">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(asset, i) in element.data.rewardAssets" :key="i">
                <td>
                  <BaseAssetFromAsset style="min-width: 72px" :asset="asset" />
                </td>
                <td class="text-left">
                  <BaseAmountFromAsset :asset="asset" />
                </td>
                <td class="text-right">
                  <BasePrice :asset="asset" />
                </td>
                <td class="text-right">
                  <BaseValue :value="asset.value" />
                </td>
              </tr>
            </tbody>
          </template>
        </v-table>
        <v-table
          v-if="element.data.borrowedAssets.length > 0"
          hover
          density="compact"
        />
      </div>
    </template>
  </PortfolioBodyElementBase>
</template>
